import {
  BasketballEventType,
  BasketballEventUserRolePlayerCoach,
  BasketballEventUserRoleType,
  BasketballGame,
  BasketballGameReply,
  BasketballPracticeReply,
  BasketballTeam,
  Notification,
  NotificationType,
  RequestBaseUpdateUserSettings,
  ResponseBaseGetDataProcessingAgreement,
  ResponseBaseGetNotificationTypes,
  ResponseBaseGetNotificationUpdates,
  ResponseBaseGetUserIcalToken,
  ResponseBaseGetUserNotifications,
  ResponseBasePostMarkTutorialAsRead,
  ResponseBasePostUserIcalToken,
  ResponseBaseUpdateUserSettings,
  ResponseBasketballUpdatePracticeNominees,
  ResponseBasketballUpdateTeamGameNominees,
  User,
  UserBasic,
  UserIcalToken,
  UserRole,
} from '@hefe/models';
import { fullUserName } from '@hefe/utils';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import API from '../../../api/api';
import { LoadingStatus } from '../../../models/slice.models';
import { isCacheValid } from '../../../utils/cache/caching';
import {
  deleteBasketballGame,
  saveBasketballGame,
  setBasketballSeasonPlanerVisibleTasks,
} from '../../basketball/actions/basketball-game/basketball-game.actions';
import { setBasketballPracticeNominees } from '../../basketball/actions/basketball-practices/basketball-practices.actions';
import { setBasketballTeamGameNominees } from '../../basketball/actions/basketball-team-game/basketball-team-game.actions';
import {
  findEventIndex,
  findUserRoleIndex,
  updateTeamReplies,
  updateUserBasketballGameReply,
  updateUserBasketballPracticeReply,
} from '../../basketball/basketball-events/basketball-events.slice';
import {
  createBasketballTeam,
  deleteBasketballTeam,
} from '../../basketball/basketball-teams/basketball-teams.slice';
import { PersistSystemSlice } from '../../system/persist-system/persist-system.slice';
import { agreeToDataProcessingAgreement } from '../club/club.slice';
import { tutorialsApi } from '../tutorials/tutorials.api';

export const USER_FEATURE_KEY = 'user';

interface UserBasicWithRoles extends UserBasic {
  role: UserRole[];
}

export interface UserState {
  user?: User;
  childUsers: UserBasicWithRoles[];
  loadingStatus: LoadingStatus;
  childUserLoadingStatus: LoadingStatus;
  loginStatus: LoadingStatus;
  error?: string;
  notifications: ResponseBaseGetUserNotifications;
  notificationTypes: NotificationType[];
  newNotificationTypes: string[];
  interfaces: {
    [key: string]: any;
  };
  expoToken?: string;
}

export const initialUserState: UserState = {
  user: undefined,
  childUsers: [],
  loadingStatus: LoadingStatus.NOT_LOADED,
  childUserLoadingStatus: LoadingStatus.NOT_LOADED,
  loginStatus: LoadingStatus.NOT_LOADED,
  error: '',
  notifications: {
    current_page: -1, // Init as -1 to check if notifications have been loaded or not
    data: [],
    from: 0,
    last_page: 0,
    links: [],
    path: '',
    per_page: 0,
    to: 0,
    total: 0,
  },
  notificationTypes: [],
  newNotificationTypes: [],
  interfaces: {},
  expoToken: undefined,
};

/**
 *  ASYNC ACTIONS
 */

export const fetchUserData = createAsyncThunk(
  'user/fetchUserData',
  async (_, thunkAPI) => {
    const response = await API.user().userdata();
    return response?.data;
  }
);

export const fetchUserChildUsers = createAsyncThunk(
  'user/fetchUserChildUsers',
  async (_, thunkAPI) => {
    const response = await API.user().childUsers();
    return response?.data;
  }
);

export const loginUser = createAsyncThunk(
  'user/login',
  async (
    data: {
      email: string;
      password: string;
    },
    thunkAPI
  ) => {
    const response = await API.user().login(data);
    return response?.data;
  }
);

export const logoutUser = createAsyncThunk(
  'user/logout',
  async (data: { token?: string }, thunkAPI) => {
    if (data.token) {
      await API.user().deleteNotificationToken(data.token);
    }
    const response = await API.user().logout();
    return response?.data;
  }
);

export const getChildUser = createAsyncThunk(
  'user/fetchUserData',
  async (data: any, thunkAPI) => {
    const response = await API.user().activeChild(data);
    return response?.data;
  }
);

export const updateBasketballUserCoachTeam = createAsyncThunk(
  'user/updateCoachTeam',
  async (teamId: number, thunkAPI) => {
    const response = await API.basketballUser().updateActiveCoachTeam({
      team_id: teamId,
    });
    return response?.data;
  }
);

export const uploadUserAvatar = createAsyncThunk(
  'user/uploadUserAvatar',
  async (
    data: {
      file: any;
      base64: boolean;
    },
    thunkAPI
  ) => {
    const response = data.base64
      ? await API.user().uploadAvatarAsBase64(data.file)
      : await API.user().uploadAvatar(data.file);
    return response?.data;
  }
);

export const deleteUserAvatar = createAsyncThunk(
  'user/deleteUserAvatar',
  async (thunkAPI) => {
    const response = await API.user().deleteAvatar();
    return response?.data;
  }
);
/*************************************************************
 *
 *                       Notifications
 *
 **************************************************************/
export const fetchUserNotifications = createAsyncThunk(
  'user/fetchUserNotifications',
  async (page: number, thunkAPI) => {
    const response = await API.user().notifications(page);
    return response?.data;
  }
);

export const markAllUserNotificationsAsRead = createAsyncThunk(
  'user/markAllNotificationsAsRead',
  async (_, thunkAPI) => {
    const response = await API.user().markNotificationsAsRead();
    return response?.data;
  }
);

export const markUserNotificationAsRead = createAsyncThunk<any, Notification>(
  'user/markNotificationAsRead',
  async (notification: Notification, thunkAPI) => {
    const response = await API.user().markNotificationAsRead(notification.id);
    if (response) {
      return thunkAPI.fulfillWithValue(notification);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const deleteAllUserNotifications = createAsyncThunk(
  'user/deleteAllNotifications',
  async (_, thunkAPI) => {
    const response = await API.user().deleteNotifications();
    return response?.data;
  }
);

export const addExpoNotificationToken = createAsyncThunk<any, string>(
  'user/addExpoNotificationToken',
  async (token: string, thunkAPI) => {
    const response = await API.user().addNotificationToken(token);
    if (response) {
      return thunkAPI.fulfillWithValue(token);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/*************************************************************
 *
 *                       User Settings
 *
 **************************************************************/

export const fetchUserNotificationTypes = createAsyncThunk(
  'user/fetchNotificationTypes',
  async (
    data: {
      cacheDuration: number;
    },
    thunkAPI
  ) => {
    if (!isCacheValid('user/fetchNotificationTypes', data, thunkAPI)) {
      const response = await API.settings().notifications();
      return response?.data;
    }
  }
);

export const fetchNotificationTypesUpdates = createAsyncThunk(
  'user/fetchNotificationTypesUpdates',
  async (_, thunkAPI) => {
    thunkAPI.dispatch(
      PersistSystemSlice.actions.setLastCalled(
        'user/fetchNotificationTypesUpdates'
      )
    );
    const response = await API.settings().notificationTypesUpdates();
    return response?.data;
  }
);

export const updateUserSettings = createAsyncThunk(
  'user/updateUserSettings',
  async (data: RequestBaseUpdateUserSettings, thunkAPI) => {
    const response = await API.user().updateSettings(data);
    return response?.data;
  }
);

export const updateUserNotifications = createAsyncThunk(
  'user/updateUserNotifications',
  async (data: any, thunkAPI) => {
    const response = await API.user().updateNotifications({
      notificationSettings: {
        notifications: data,
      },
    });
    return response?.data;
  }
);

export const fetchUserIcalInterface = createAsyncThunk(
  'user/fetchIcalInterface',
  async (
    data: {
      cacheDuration: number;
    },
    thunkAPI
  ) => {
    if (!isCacheValid('user/fetchIcalInterface', data, thunkAPI)) {
      const response = await API.basketballUser().getIcalToken();
      return response?.data;
    }
  }
);

export const createUserIcalInterface = createAsyncThunk(
  'user/createIcalInterface',
  async (thunkAPI) => {
    const response = await API.basketballUser().generateIcalToken();
    return response?.data;
  }
);

/**
 *    SLICE AND REDUCER
 */

export const userSlice = createSlice({
  name: USER_FEATURE_KEY,
  initialState: initialUserState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state: UserState) => {
        state.loadingStatus = LoadingStatus.LOADING;
      })
      .addCase(
        fetchUserData.fulfilled,
        (state: UserState, action: PayloadAction<User>) => {
          state.user = action.payload;
          state.loadingStatus = LoadingStatus.LOADED;
        }
      )
      .addCase(fetchUserData.rejected, (state: UserState, action) => {
        state.loadingStatus = LoadingStatus.ERROR;
        state.error = action.error.message;
      })
      .addCase(loginUser.pending, (state: UserState) => {
        state.loginStatus = LoadingStatus.LOADING;
      })
      .addCase(
        loginUser.fulfilled,
        (state: UserState, action: PayloadAction<any>) => {
          state.user = action.payload.user;
          state.loginStatus = LoadingStatus.LOADED;
        }
      )
      .addCase(loginUser.rejected, (state: UserState, action) => {
        state.loginStatus = LoadingStatus.ERROR;
      })
      .addCase(fetchUserChildUsers.pending, (state: UserState, action) => {
        state.childUserLoadingStatus = LoadingStatus.LOADING;
      })
      .addCase(fetchUserChildUsers.fulfilled, (state: UserState, action) => {
        state.childUsers = action.payload;
        state.childUserLoadingStatus = LoadingStatus.LOADED;
      })
      .addCase(fetchUserChildUsers.rejected, (state: UserState, action) => {
        state.childUserLoadingStatus = LoadingStatus.ERROR;
      })
      .addCase(logoutUser.fulfilled, (state: UserState) => initialUserState)
      /**
       *  BASKETBALL EVENTS
       */
      .addCase(
        updateBasketballUserCoachTeam.fulfilled,
        (state: UserState, action: PayloadAction<User>) => {
          if (state.user?.basketball_user) {
            state.user.basketball_user.active_coach_team_id =
              action.payload.basketball_user?.active_coach_team_id;
          }
        }
      )
      .addCase(
        createBasketballTeam.fulfilled,
        (state: UserState, action: PayloadAction<{ data: BasketballTeam }>) => {
          if (state.user?.basketball_user?.coached_teams) {
            state.user.basketball_user.coached_teams.push(action.payload.data);
          }
        }
      )
      .addCase(
        deleteBasketballTeam.fulfilled,
        (state: UserState, action: PayloadAction<BasketballTeam>) => {
          if (state.user?.basketball_user?.coached_teams) {
            state.user.basketball_user.coached_teams =
              state.user.basketball_user.coached_teams.filter(
                (team) => team.id !== action.payload.id
              );
          }
        }
      )
      .addCase(
        updateUserBasketballGameReply.fulfilled,
        (state: UserState, action: PayloadAction<BasketballGameReply>) => {
          const reply = action.payload;
          if (state.user?.basketball_user?.upcomingEvents) {
            const index = findUserRoleIndex(
              state.user.basketball_user.upcomingEvents,
              BasketballEventType.GAME,
              reply.game_id,
              BasketballEventUserRoleType.PLAYER,
              reply.repliable_id
            );

            if (index.key === '') return;

            const userRole = state.user?.basketball_user?.upcomingEvents[
              index.key
            ][index.eventIndex]?.user_data[index.userIndex]?.roles[
              index.roleIndex
            ] as BasketballEventUserRolePlayerCoach;

            if (userRole?.reply) {
              userRole.reply.reply = reply?.reply;
            }
            updateTeamReplies(
              state.user.basketball_user.upcomingEvents[index.key][
                index.eventIndex
              ],
              reply
            );
          }
        }
      )
      .addCase(
        updateUserBasketballPracticeReply.fulfilled,
        (state: UserState, action: PayloadAction<BasketballPracticeReply>) => {
          const reply = action.payload;
          if (state.user?.basketball_user?.upcomingEvents) {
            const index = findUserRoleIndex(
              state.user!.basketball_user!.upcomingEvents,
              BasketballEventType.PRACTICE,
              reply.practice_id,
              BasketballEventUserRoleType.PLAYER,
              reply.repliable_id
            );

            if (index.key === '') return;

            const userRole = state.user?.basketball_user?.upcomingEvents[
              index.key
            ][index.eventIndex]?.user_data[index.userIndex]?.roles[
              index.roleIndex
            ] as BasketballEventUserRolePlayerCoach;

            if (userRole?.reply) {
              userRole.reply.reply = reply?.reply;
            }
            updateTeamReplies(
              state.user.basketball_user.upcomingEvents[index.key][
                index.eventIndex
              ],
              reply
            );
          }
        }
      );
    builder.addCase(
      uploadUserAvatar.fulfilled,
      (state: UserState, action: PayloadAction<User>) => {
        if (action.payload) {
          if (state.user) {
            state.user.avatar = action.payload.avatar;
          }
        }
      }
    );
    builder.addCase(
      deleteUserAvatar.fulfilled,
      (state: UserState, action: PayloadAction<User>) => {
        if (state.user) {
          state.user.avatar = undefined;
        }
      }
    );
    builder.addCase(
      fetchUserNotificationTypes.fulfilled,
      (
        state: UserState,
        action: PayloadAction<ResponseBaseGetNotificationTypes>
      ) => {
        if (state.user) {
          state.notificationTypes = getNotificationTypesWithUpdatedKeys(
            action.payload,
            ''
          );
        }
      }
    );
    builder.addCase(
      fetchNotificationTypesUpdates.fulfilled,
      (
        state: UserState,
        action: PayloadAction<ResponseBaseGetNotificationUpdates>
      ) => {
        if (state.user) {
          state.newNotificationTypes = action.payload.new_categories;
        }
      }
    );
    builder.addCase(
      updateUserSettings.fulfilled,
      (
        state: UserState,
        action: PayloadAction<ResponseBaseUpdateUserSettings>
      ) => {
        if (state.user) {
          state.user.user_settings = action.payload;
        }
      }
    );
    builder.addCase(
      updateUserNotifications.fulfilled,
      (state: UserState, action: PayloadAction<User>) => {
        if (state.user) {
          state.user.user_settings.notifications =
            action.payload.user_settings.notifications;
        }
      }
    );
    builder.addCase(
      fetchUserIcalInterface.fulfilled,
      (
        state: UserState,
        action: PayloadAction<ResponseBaseGetUserIcalToken>
      ) => {
        if (action.payload) {
          state.interfaces['ical'] = action.payload;
        }
      }
    );
    builder.addCase(
      createUserIcalInterface.fulfilled,
      (
        state: UserState,
        action: PayloadAction<ResponseBasePostUserIcalToken>
      ) => {
        if (action.payload) {
          state.interfaces['ical'] = action.payload;
        }
      }
    );
    builder.addCase(
      fetchUserNotifications.fulfilled,
      (
        state: UserState,
        action: PayloadAction<ResponseBaseGetUserNotifications>
      ) => {
        const page = action.payload.current_page;
        if (page === 1) {
          state.notifications = action.payload;
        } else {
          // keep old notifications and put new to the end of data array
          state.notifications = {
            ...action.payload,
            data: [...state.notifications.data, ...action.payload.data],
          };
        }
      }
    );
    builder.addCase(
      markUserNotificationAsRead.fulfilled,
      (state: UserState, action: PayloadAction<Notification>) => {
        state.notifications.data = state.notifications.data.map((n) => {
          if (n.id === action.payload.id) {
            return {
              ...n,
              read_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };
          }
          return n;
        });

        // User object
        if (state.user) {
          state.user.notifications.last5 = state.user.notifications.last5.map(
            (n) => {
              if (n.id === action.payload.id) {
                return {
                  ...n,
                  read_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                };
              }
              return n;
            }
          );
          state.user.notifications.unread =
            state.user.notifications.unread.filter(
              (n) => n.id !== action.payload.id
            );
        }
      }
    );
    builder.addCase(
      markAllUserNotificationsAsRead.fulfilled,
      (state: UserState) => {
        state.notifications.data = state.notifications.data.map(
          (notification) => {
            return {
              ...notification,
              read_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };
          }
        );

        // User object
        if (state.user) {
          state.user.notifications.last5 = state.user.notifications.last5.map(
            (notification) => {
              return {
                ...notification,
                read_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
              };
            }
          );
          state.user.notifications.unread = state.user.notifications.unread =
            [];
        }
      }
    );
    builder.addCase(
      deleteAllUserNotifications.fulfilled,
      (state: UserState) => {
        state.notifications = initialUserState.notifications;
        if (state.user) {
          state.user.notifications.last5 = [];
          state.user.notifications.unread = [];
        }
      }
    );
    builder.addCase(
      setBasketballSeasonPlanerVisibleTasks.fulfilled,
      (state: UserState, action: PayloadAction<number[]>) => {
        if (state.user && action.payload) {
          state.user.club_settings.basketball.season_planer.visible_tasks =
            action.payload;
        }
      }
    );
    builder.addCase(
      agreeToDataProcessingAgreement.fulfilled,
      (
        state: UserState,
        action: PayloadAction<ResponseBaseGetDataProcessingAgreement>
      ) => {
        if (state.user && action.payload) {
          state.user.hasUnacceptedDPA = false;
        }
      }
    );
    /***************************************************************
     *                      GAMES
     **************************************************************/
    builder.addCase(
      saveBasketballGame.fulfilled,
      (state, action: PayloadAction<BasketballGame>) => {
        if (action.payload && state.user?.basketball_user?.upcomingEvents) {
          const game = action.payload;
          const index = findEventIndex(
            state.user?.basketball_user?.upcomingEvents,
            BasketballEventType.GAME,
            game.id
          );

          if (index.key === '') return;

          if (game.event) {
            state.user.basketball_user.upcomingEvents[index.key][index.index] =
              game.event;
          }
        }
      }
    );
    builder.addCase(
      deleteBasketballGame.fulfilled,
      (state, action: PayloadAction<number>) => {
        if (action.payload && state.user?.basketball_user?.upcomingEvents) {
          const index = findEventIndex(
            state.user?.basketball_user?.upcomingEvents,
            BasketballEventType.GAME,
            action.payload
          );

          if (index.key === '') return;

          delete state.user.basketball_user.upcomingEvents[index.key][
            index.index
          ];
        }
      }
    );
    /**********************************************************************
     *                      MATCHERS
     **********************************************************************/
    builder.addMatcher(
      isAnyOf(
        setBasketballTeamGameNominees.fulfilled,
        setBasketballPracticeNominees.fulfilled
      ),
      (
        state,
        action: PayloadAction<
          | ResponseBasketballUpdateTeamGameNominees
          | ResponseBasketballUpdatePracticeNominees
        >
      ) => {
        if (!state.user?.basketball_user?.upcomingEvents) {
          return;
        }

        let eventId = 0;
        let eventType: BasketballEventType = BasketballEventType.GAME;

        if (action.type === setBasketballTeamGameNominees.fulfilled.type) {
          eventId = (action.payload as ResponseBasketballUpdateTeamGameNominees)
            .game_id;
          eventType = BasketballEventType.GAME;
        } else {
          eventId = (action.payload as ResponseBasketballUpdatePracticeNominees)
            .practice_id;
          eventType = BasketballEventType.PRACTICE;
        }

        const response = action.payload;
        const index = findEventIndex(
          state.user?.basketball_user?.upcomingEvents ?? {},
          eventType,
          eventId
        );

        if (index.key === '') return;

        const game =
          state.user?.basketball_user?.upcomingEvents[index.key][index.index];

        const team = game.teams.find((t) => t.id === response.team_id);
        if (team) {
          team.replies = {
            yes: response.player_replies.yes.length,
            no: response.player_replies.no.length,
            unknown: response.player_replies.unknown.length,
            not_nominated: response.player_replies.not_nominated.length,
          };
        }

        // update user role
        for (const user of game.user_data) {
          for (const role of user.roles) {
            if (role.type === BasketballEventUserRoleType.PLAYER) {
              (role as BasketballEventUserRolePlayerCoach).nominated =
                response.nominees.includes(user.user.id);
            }
          }
        }
      }
    );
    builder.addMatcher(
      isAnyOf(tutorialsApi.endpoints.markTutorialAsRead.matchFulfilled),
      (
        state: UserState,
        action: PayloadAction<ResponseBasePostMarkTutorialAsRead>
      ) => {
        if (state.user?.user_settings?.dismissed_content) {
          state.user.user_settings.dismissed_content.tutorials =
            action.payload.tutorials;
        }

        if (state.user?.unread?.tutorials) {
          state.user.unread.tutorials = state.user.unread.tutorials.filter(
            (tutorial) => !action.payload.tutorials.includes(tutorial.id)
          );
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const userReducer = userSlice.reducer;

export const userActions = userSlice.actions;

export const getUserState = (rootState: any): UserState =>
  rootState[USER_FEATURE_KEY];

export const userLoadingStatus = createSelector(
  getUserState,
  (state) => state.loadingStatus
);
export const userState = createSelector(getUserState, (state) => state);

export const currentUserSelector = createSelector(
  getUserState,
  (state) => state.user
);

export const currentUserIsAdminSelector = createSelector(
  getUserState,
  (state) => {
    let returnValue = false;

    if (state.user?.role.find((role) => role === UserRole.BB_ADMIN)) {
      returnValue = true;
    }

    return returnValue;
  }
);

export const currentUserIsVehicleAdminSelector = createSelector(
  getUserState,
  (state) => {
    let returnValue = false;

    if (state.user?.role.find((role) => role === UserRole.VEHICLE_ADMIN)) {
      returnValue = true;
    }

    return returnValue;
  }
);

export const currentUserChildUsersSelector = createSelector(
  getUserState,
  (state) => state.childUsers
);

export const currentUserAndChildUsersRefereesSelectSelctor = createSelector(
  currentUserSelector,
  currentUserChildUsersSelector,
  (currentUser, childUsers) => {
    return [currentUser, ...childUsers]
      .filter((user) => user?.role.includes(UserRole.BB_REFEREE))
      .map((user) => ({
        value: user?.id,
        label: fullUserName(user),
      }));
  }
);

export const userActiveBasketballCoachTeamId = createSelector(
  getUserState,
  (state) => {
    return state.user?.basketball_user?.active_coach_team_id;
  }
);

export const userBasketballCoachTeamIdsSelector = createSelector(
  getUserState,
  (state) => {
    return state.user?.basketball_user?.coached_teams?.map((team) => team.id);
  }
);

export const notificationTypesSelector = createSelector(
  getUserState,
  (state) => {
    return state.notificationTypes;
  }
);

export const newNotificationTypesSelector = createSelector(
  getUserState,
  (state) => {
    return state.newNotificationTypes;
  }
);

export const userIcalInterfaceSelector = createSelector(
  getUserState,
  (state) => {
    return state.interfaces['ical'] as UserIcalToken;
  }
);

export const userNotificationsSelector = createSelector(
  getUserState,
  (state) => {
    return state.notifications;
  }
);

/**
 *  Helper functions
 */
const getNotificationTypesWithUpdatedKeys = (
  types: NotificationType[],
  parentKey: string
): NotificationType[] => {
  return types.map((type) => {
    const key = parentKey !== '' ? `${parentKey}.${type.key}` : type.key;

    return {
      ...type,
      key: key,
      children: type.children
        ? getNotificationTypesWithUpdatedKeys(type.children, key)
        : undefined,
    };
  });
};
